import React, { useMemo, useState } from 'react';
import ResponsiveDialogue from '../../ResponsiveDialogue/ResponsiveDialogue';
import DataGridProComponent from '../../DataGrid/DataGridProComponent';
import { auditLogAssetSpecifications } from '../../DataGrid/columnDefinitions.tsx';
import RoundedButton from '../../RoundedButton/RoundedButton.tsx';
import { Box } from '@mui/material';
import { useLazyGetAuditLogsQuery } from '../../../api/asset.api';
import { UpdateHistoryDataGridType } from '../../DataGrid/DataGridTypes.ts';
import type { GridDataSource } from '@mui/x-data-grid-pro';

interface AuditLogProps {
	open: boolean;
	onClose: () => void;
	asset: any;
	backToAssetDetails: () => void;
	backToAssetList: () => void;
}

const AuditLog: React.FC<AuditLogProps> = ({
	open,
	onClose,
	asset,
	backToAssetDetails,
	backToAssetList,
}) => {
	const [getAuditLogs] = useLazyGetAuditLogsQuery();
	const [dataIsLoading, setDataIsLoading] = useState(false);

	const auditLogDataSource: GridDataSource = useMemo(
		() => ({
			getRows: async ({ paginationModel, sortModel }) => {
				if (!asset?.instItemAssetId) {
					return { rows: [], rowCount: 0 };
				}
				setDataIsLoading(true);
				try {
					const params = {
						assetId: asset.instItemAssetId,
						page: paginationModel?.page ?? 0,
						pageSize: paginationModel?.pageSize ?? 10,
						sortDirection: sortModel?.[0]?.sort || '',
						sortField: sortModel?.[0]?.field || '',
					};

					const result = await getAuditLogs(params, true).unwrap();
					setDataIsLoading(false);
					return {
						rows: result?.content ?? [],
						rowCount: result?.totalElements ?? 0,
					};
				} catch (error) {
					return { rows: [], rowCount: 0 };
				} finally {
					setDataIsLoading(false);
				}
			},
		}),
		[asset?.instItemAssetId]
	);

	return (
		<ResponsiveDialogue
			data-testid="audit-log-dialogue"
			open={open}
			onClose={onClose}
			title="Audit Log"
		>
			<Box data-testid="audit-log-data-grid">
				<DataGridProComponent
					columns={auditLogAssetSpecifications}
					dataGridType={UpdateHistoryDataGridType}
					dataSource={auditLogDataSource}
					dataIsLoading={dataIsLoading}
					checkboxEnabled={false}
				/>
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }}>
				<Box sx={{ pr: 2 }}>
					<RoundedButton
						variantStyle="light"
						variant="outlined"
						data-testid="back-to-asset-detail-button"
						onClick={backToAssetDetails}
					>
						Back to Asset Details
					</RoundedButton>
				</Box>
				<Box>
					<RoundedButton
						variantStyle="dark"
						variant="contained"
						data-testid="continue-to-asset-list-button"
						onClick={backToAssetList}
					>
						Continue to Asset list
					</RoundedButton>
				</Box>
			</Box>
		</ResponsiveDialogue>
	);
};

export default AuditLog;
